'use client';

import { cn } from '@module/lib/utils';
import { type EmoticonFilterState, useEmoticonFilterStore } from '@module/stores/useEmoticonFilterStore';
import useModalStore from '@module/stores/useModalStore';
import BaseButton from '@pc/components/atoms/button/BaseButton';
import { Input } from '@pc/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pc/components/ui/select';
import { useEffect } from 'react';

const clampToRange = (v: string, min = 0, max = 999_999_999) => String(Math.max(min, Math.min(max, Number(v))));

interface SignatureEmoticonFilterInputsProps {
  channelId?: string;
}

function SignatureEmoticonFilterInputs({ channelId = '' }: SignatureEmoticonFilterInputsProps) {
  const pushModal = useModalStore((state) => state.pushModal);

  const searchTypeSelectValue = useEmoticonFilterStore((state) => state.searchTypeSelectValue);
  const bjIdInputValue = useEmoticonFilterStore((state) => state.bjIdInputValue);
  const minHeartInputValue = useEmoticonFilterStore((state) => state.minHeartInputValue);
  const maxHeartInputValue = useEmoticonFilterStore((state) => state.maxHeartInputValue);
  const setState = useEmoticonFilterStore((state) => state.setState);
  useEffect(() => {
    if (channelId) {
      setState('bjIdInputValue', channelId);
    }
  }, [channelId]);

  const handleSearchTypeSelectValueChange = (value: EmoticonFilterState['searchTypeSelectValue']) => {
    setState('bjIdInputValue', '');
    setState('minHeartInputValue', '');
    setState('maxHeartInputValue', '');
    setState('searchTypeSelectValue', value);
  };

  const handleSearchClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (searchTypeSelectValue === 'bj' && bjIdInputValue.length === 1) {
      pushModal({
        type: 'CONFIRM_MODAL',
        options: { description: '검색어는 2글자 이상 입력해 주세요.' },
      });
      return;
    }

    // 최소값이 최대값보다 크면 최소값과 최대값을 교체
    const isNumberReversed =
      minHeartInputValue !== '' && maxHeartInputValue !== '' && Number(minHeartInputValue) > Number(maxHeartInputValue);
    if (isNumberReversed) {
      setState('minHeart', maxHeartInputValue);
      setState('maxHeart', minHeartInputValue);
      setState('minHeartInputValue', maxHeartInputValue);
      setState('maxHeartInputValue', minHeartInputValue);
    } else {
      setState('minHeart', minHeartInputValue);
      setState('maxHeart', maxHeartInputValue);
    }

    setState('searchType', searchTypeSelectValue);
    setState('bjId', bjIdInputValue);

    // 검색값 외 필터링 초기화
    setState('page', 1);
    setState('heartCategory', 'all');
  };

  return (
    <form className='flex' onSubmit={handleSearchClick}>
      {!channelId && (
        <Select defaultValue='bj' onValueChange={handleSearchTypeSelectValueChange}>
          <SelectTrigger className='h-12 w-[120px] bg-transparent'>
            <SelectValue placeholder='' />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value='bj'>BJ</SelectItem>
            <SelectItem value='heart'>하트수</SelectItem>
          </SelectContent>
        </Select>
      )}

      {searchTypeSelectValue === 'bj' && !channelId ? (
        <Input
          containerClassName='mx-2 w-[254px]'
          className='px-[17px]'
          placeholder='검색어를 입력하세요.'
          value={bjIdInputValue}
          onChange={(e) => setState('bjIdInputValue', e.currentTarget.value)}
        />
      ) : (
        <div className='flex items-center gap-2'>
          <Input
            type='text'
            containerClassName={cn('mx-2 w-[107px]', { 'w-[160px]': channelId })}
            className='px-[17px]'
            placeholder={channelId ? '하트수 최소' : '최소'}
            value={minHeartInputValue}
            min={0}
            max={999999999}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.currentTarget.value;
              if (value === '') {
                setState('minHeartInputValue', '');
                return;
              }
              if (Number.isNaN(Number(value))) return;
              setState('minHeartInputValue', clampToRange(value));
            }}
          />
          <span className='text-baseFg'>~</span>
          <Input
            type='text'
            containerClassName={cn('mx-2 w-[107px]', { 'w-[160px]': channelId })}
            className='px-[17px]'
            placeholder={channelId ? '하트수 최대' : '최대'}
            value={maxHeartInputValue}
            min={0}
            max={999999999}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.currentTarget.value;
              if (value === '') {
                setState('maxHeartInputValue', '');
                return;
              }
              if (Number.isNaN(Number(value))) return;
              setState('maxHeartInputValue', clampToRange(value));
            }}
          />
        </div>
      )}
      <BaseButton type='submit' variant='primary' size='md' className='w-[68px] text-[17px]'>
        {'검색'}
      </BaseButton>
    </form>
  );
}

export default SignatureEmoticonFilterInputs;
